import { Plan, PlansList } from '../../interfaces/tariffs.interface';

export type LegacySubscriptionPlansList = Pick<
  PlansList,
  'standart-month' | 'standart-year' | 'professional-month' | 'professional-year'
>;
import {
  SubscribePeriodEnum,
  SubscriptionType,
} from '../../pages/workspace/features/create-workspace/services/workspace-api.service';
import { ProjectTariffsData, ProjectTariffsDescriptionMap } from '../../shared/services/api/project-tariffs.service';
import {
  DEFAULT_GA_ECOMMERCE_CURRENCY,
  GaEcommerceCheckoutContext,
  GaEcommerceCurrency,
  GaEcommerceItem,
  GA_ECOMMERCE_CHECKOUT_STORAGE_KEY,
} from './ga-ecommerce.types';
function isYearlyPeriod(period: SubscribePeriodEnum | 'month' | 'year'): boolean {
  return period === SubscribePeriodEnum.YEAR || String(period) === 'year';
}

export function buildSubscriptionItemId(
  subscriptionType: SubscriptionType | string,
  period: SubscribePeriodEnum | 'month' | 'year',
): string {
  const type = String(subscriptionType).toLowerCase();
  const periodKey = isYearlyPeriod(period) ? 'year' : 'month';
  return `${type}_${periodKey}`;
}

export function subscriptionDisplayName(subscriptionType: SubscriptionType | string): string {
  const key = String(subscriptionType).toLowerCase() as keyof typeof ProjectTariffsDescriptionMap;
  return ProjectTariffsDescriptionMap[key]?.title ?? String(subscriptionType);
}

export function buildSubscriptionItem(params: {
  subscriptionType: SubscriptionType | string;
  period: SubscribePeriodEnum | 'month' | 'year';
  seats: number;
  totalValue: number;
  itemCategory?: string;
}): GaEcommerceItem {
  const quantity = Math.max(1, params.seats);
  const price = quantity > 0 ? params.totalValue / quantity : params.totalValue;

  return {
    item_id: buildSubscriptionItemId(params.subscriptionType, params.period),
    item_name: subscriptionDisplayName(params.subscriptionType),
    price: roundMoney(price),
    quantity,
    item_category: params.itemCategory ?? 'subscription',
    item_variant: isYearlyPeriod(params.period) ? 'yearly' : 'monthly',
  };
}

export function planToGaItem(plan: Plan, quantity = 1): GaEcommerceItem {
  const period = plan.isYearly ? 'year' : 'month';
  const type = plan.type.replace('-year', '').replace('-month', '');

  return {
    item_id: `${type}_${period}`,
    item_name: plan.name,
    price: roundMoney(plan.totalPrice),
    quantity: Math.max(1, quantity),
    item_category: 'subscription',
    item_variant: plan.isYearly ? 'yearly' : 'monthly',
  };
}

export function plansListToGaItems(tariffsData: LegacySubscriptionPlansList): GaEcommerceItem[] {
  const keys: (keyof LegacySubscriptionPlansList)[] = [
    'standart-month',
    'standart-year',
    'professional-month',
    'professional-year',
  ];

  return keys
    .map((key) => tariffsData[key])
    .filter((plan): plan is Plan => !!plan)
    .map((plan) => planToGaItem(plan, 1));
}

export function projectTariffsToGaItems(
  tariffData: ProjectTariffsData,
  period: 'month' | 'year',
  seats = 1,
): GaEcommerceItem[] {
  const periodData = tariffData[period];
  if (!periodData) {
    return [];
  }

  const entries: Array<{ key: string; price: number }> = [
    { key: 'free', price: periodData.free ?? 0 },
    { key: 'basic', price: periodData.basic ?? 0 },
    { key: 'standart', price: periodData.standart ?? periodData.basic ?? 0 },
    { key: 'professional', price: periodData.professional ?? 0 },
    { key: 'pro', price: periodData.pro ?? 0 },
    { key: 'enterprise', price: periodData.enterprise ?? 0 },
  ];

  return entries
    .filter((e) => e.price !== undefined)
    .map((e) => {
      const subType = e.key as SubscriptionType;
      const dailyRate = e.price * Math.max(1, seats);
      const totalValue = period === 'year' ? dailyRate * 365 : dailyRate * 30;

      return buildSubscriptionItem({
        subscriptionType: subType,
        period,
        seats,
        totalValue,
      });
    });
}

export function freeVsBasicGaItems(): GaEcommerceItem[] {
  return [
    {
      item_id: 'free_month',
      item_name: 'Free',
      price: 0,
      quantity: 1,
      item_category: 'subscription',
      item_variant: 'monthly',
    },
    {
      item_id: 'basic_month',
      item_name: 'Basic',
      price: 349000,
      quantity: 1,
      item_category: 'subscription',
      item_variant: 'monthly',
    },
  ];
}

export function tokenPlanToGaItem(tokenCount: number, totalPrice: number): GaEcommerceItem {
  return {
    item_id: 'tokens_pack',
    item_name: 'AI Tokens',
    price: roundMoney(totalPrice),
    quantity: Math.max(1, tokenCount),
    item_category: 'tokens',
  };
}

export function extractTransactionIdFromPayLink(payLink: string): string {
  if (!payLink) {
    return `checkout_${Date.now()}`;
  }

  try {
    const url = new URL(payLink);
    const candidates = [
      url.searchParams.get('transaction_id'),
      url.searchParams.get('transactionId'),
      url.searchParams.get('id'),
      url.searchParams.get('payment_id'),
      url.pathname.split('/').filter(Boolean).pop(),
    ].filter((v): v is string => !!v && v.length > 0);

    if (candidates.length > 0) {
      return candidates[0]!.slice(0, 64);
    }
  } catch {
    // not a valid URL — fall through
  }

  let hash = 0;
  for (let i = 0; i < payLink.length; i++) {
    hash = (hash << 5) - hash + payLink.charCodeAt(i);
    hash |= 0;
  }
  return `pay_${Math.abs(hash)}`;
}

export function saveGaCheckoutContext(context: GaEcommerceCheckoutContext): void {
  try {
    sessionStorage.setItem(GA_ECOMMERCE_CHECKOUT_STORAGE_KEY, JSON.stringify(context));
  } catch {
    // ignore quota / private mode
  }
}

export function loadGaCheckoutContext(payLink: string): GaEcommerceCheckoutContext | null {
  try {
    const raw = sessionStorage.getItem(GA_ECOMMERCE_CHECKOUT_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as GaEcommerceCheckoutContext;
    if (parsed?.payLink && parsed.payLink === payLink) {
      return parsed;
    }
  } catch {
    // ignore
  }
  return null;
}

export function clearGaCheckoutContext(): void {
  try {
    sessionStorage.removeItem(GA_ECOMMERCE_CHECKOUT_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function normalizeCurrency(currency?: string): GaEcommerceCurrency {
  return currency === 'IDR' ? 'IDR' : DEFAULT_GA_ECOMMERCE_CURRENCY;
}
